import { useEffect, useState } from "react";
import {
  listMovements,
  listWallets,
  listWalletMovements,
  type CurrencyVolume,
  type Wallet,
  type WalletMovement,
  type WalletMovementFull,
} from "@/lib/api";
import { formatAmount, formatDate, formatRelative } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, type ColumnDef } from "@/components/DataTable";
import { CreditCard, Smartphone } from "lucide-react";

export function WalletsPage() {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Wallet | null>(null);
  const [movements, setMovements] = useState<WalletMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [currencyMovements, setCurrencyMovements] = useState<WalletMovementFull[]>([]);
  const [currencyMovementsLoading, setCurrencyMovementsLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState<"" | "mobile_money" | "cards">("");
  const [currencyFilter, setCurrencyFilter] = useState<"" | "CDF" | "USD">("");

  useEffect(() => {
    listWallets()
      .then((r) => setWallets(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selected) {
      setMovementsLoading(true);
      listWalletMovements(selected.id, { limit: 50 })
        .then((r) => setMovements(r.data))
        .finally(() => setMovementsLoading(false));
    }
  }, [selected]);

  useEffect(() => {
    if (!selectedCurrency) {
      setCurrencyMovements([]);
      return;
    }
    setCurrencyMovementsLoading(true);
    listMovements({ currency: selectedCurrency, limit: 100 })
      .then((r) => setCurrencyMovements(r.data))
      .finally(() => setCurrencyMovementsLoading(false));
  }, [selectedCurrency]);

  const totalsByCurrency = sumByCurrency(wallets, (w) => ({ currency: w.currency, amount_minor: w.balance_minor }));
  const reservedByCurrency = sumByCurrency(wallets, (w) => ({ currency: w.currency, amount_minor: w.reserved_balance_minor }));
  const reservedMap = new Map(reservedByCurrency.map((r) => [r.currency, r.amount_minor]));
  const currencyCards = [...totalsByCurrency].sort((a, b) => a.currency.localeCompare(b.currency));

  const filteredWallets = wallets.filter(
    (w) =>
      (!typeFilter || w.type === typeFilter) &&
      (!currencyFilter || w.currency === currencyFilter),
  );

  const walletCols: ColumnDef<Wallet>[] = [
    {
      key: "merchant",
      header: "Marchand",
      minWidth: 280,
      sortable: true,
      accessor: (w) => w.business_name,
      cell: (w) => (
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            w.type === "mobile_money" ? "bg-success-50 text-success-700" : "bg-info-50 text-info-700"
          }`}>
            {w.type === "mobile_money" ? <Smartphone size={16} /> : <CreditCard size={16} />}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-[#181c32] truncate">{w.business_name}</div>
            <div className="text-xs text-[#7e8299]">{w.type === "mobile_money" ? "Mobile Money" : "Cartes"}</div>
          </div>
        </div>
      ),
    },
    {
      key: "currency",
      header: "Devise",
      minWidth: 90,
      sortable: true,
      accessor: (w) => w.currency,
      cell: (w) => <span className="font-semibold">{w.currency}</span>,
    },
    {
      key: "balance",
      header: "Disponible",
      align: "right",
      minWidth: 130,
      sortable: true,
      accessor: (w) => w.balance_minor,
      cell: (w) => <span className="font-bold text-[#181c32]">{formatAmount(w.balance_minor, w.currency)}</span>,
    },
    {
      key: "reserved",
      header: "Réservé",
      align: "right",
      minWidth: 110,
      sortable: true,
      accessor: (w) => w.reserved_balance_minor,
      cell: (w) => <span className="text-[#7e8299]">{formatAmount(w.reserved_balance_minor, w.currency)}</span>,
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      minWidth: 110,
      sortable: true,
      accessor: (w) => w.balance_minor + w.reserved_balance_minor,
      cell: (w) => <span>{formatAmount(w.balance_minor + w.reserved_balance_minor, w.currency)}</span>,
    },
    {
      key: "updated",
      header: "Mis à jour",
      minWidth: 110,
      sortable: true,
      accessor: (w) => w.updated_at,
      cell: (w) => <span className="text-xs text-[#7e8299]">{formatRelative(w.updated_at)}</span>,
    },
  ];

  const currencyMovementsCols: ColumnDef<WalletMovementFull>[] = [
    {
      key: "date",
      header: "Date",
      minWidth: 140,
      sortable: true,
      accessor: (m) => m.created_at,
      cell: (m) => (
        <span className="text-xs text-[#7e8299]" title={formatDate(m.created_at)}>
          {formatRelative(m.created_at)}
        </span>
      ),
    },
    {
      key: "wallet",
      header: "Wallet",
      minWidth: 140,
      cell: (m) => (
        <div className="text-xs">
          <div className="font-semibold text-[#181c32] capitalize">
            {m.wallet_type === "mobile_money" ? "Mobile Money" : "Cartes"}
          </div>
          <div className="text-[#7e8299]">{m.wallet_currency}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      minWidth: 140,
      cell: (m) => <Badge color={m.amount_minor >= 0 ? "success" : "danger"}>{m.display_type ?? m.type}</Badge>,
    },
    {
      key: "amount",
      header: "Montant",
      align: "right",
      minWidth: 120,
      sortable: true,
      accessor: (m) => m.amount_minor,
      cell: (m) => (
        <span className={`font-bold ${m.amount_minor >= 0 ? "text-success-700" : "text-danger-700"}`}>
          {m.amount_minor >= 0 ? "+" : ""}
          {formatAmount(m.amount_minor, m.currency)}
        </span>
      ),
    },
    {
      key: "balance_after",
      header: "Solde après",
      align: "right",
      minWidth: 130,
      cell: (m) => (
        <span className="font-semibold text-[#181c32]">
          {formatAmount(m.balance_after_minor, m.currency)}
        </span>
      ),
    },
    {
      key: "ref",
      header: "Référence",
      minWidth: 180,
      cell: (m) => (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#7e8299]">{m.reference_type}</div>
          <div className="font-mono text-xs text-[#3f4254]">{m.reference_id.slice(0, 20)}…</div>
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      minWidth: 220,
      wrap: true,
      cell: (m) => (
        <span className="text-xs text-[#7e8299]">{m.description ?? "—"}</span>
      ),
    },
  ];

  const movementCols: ColumnDef<WalletMovement>[] = [
    {
      key: "type",
      header: "Type",
      cell: (m) => <Badge color={m.amount_minor >= 0 ? "success" : "danger"}>{m.type}</Badge>,
    },
    {
      key: "amount",
      header: "Montant",
      align: "right",
      cell: (m) => {
        const currency = selected?.currency ?? "CDF";
        const sign = m.amount_minor >= 0 ? "+" : "";
        return (
          <span className={`font-bold ${m.amount_minor >= 0 ? "text-success-700" : "text-danger-700"}`}>
            {sign}
            {formatAmount(m.amount_minor, currency)}
          </span>
        );
      },
    },
    {
      key: "balance_after",
      header: "Solde après",
      align: "right",
      cell: (m) => (
        <span className="font-semibold text-[#181c32]">
          {formatAmount(m.balance_after_minor, selected?.currency ?? "CDF")}
        </span>
      ),
    },
    {
      key: "ref",
      header: "Référence",
      cell: (m) => (
        <div>
          <div className="text-xs text-[#7e8299]">{m.reference_type}</div>
          <div className="font-mono text-xs">{m.reference_id.slice(0, 14)}…</div>
        </div>
      ),
    },
    {
      key: "date",
      header: "Date",
      cell: (m) => <span className="text-xs text-[#7e8299]" title={formatDate(m.created_at)}>{formatRelative(m.created_at)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Wallets marchands"
        breadcrumbs={[{ label: "Finance" }, { label: "Wallets" }]}
      />

      {/* KPI: one card per currency (clickable to filter movements) */}
      {currencyCards.length > 0 && (
        <div
          className="grid grid-cols-1 gap-5 mb-6"
          style={{ gridTemplateColumns: `repeat(${Math.min(currencyCards.length, 4)}, minmax(0, 1fr))` }}
        >
          {currencyCards.map((c) => (
            <CurrencyStatCard
              key={c.currency}
              currency={c.currency}
              availableMinor={c.amount_minor}
              reservedMinor={reservedMap.get(c.currency) ?? 0}
              selected={selectedCurrency === c.currency}
              onClick={() =>
                setSelectedCurrency(selectedCurrency === c.currency ? null : c.currency)
              }
            />
          ))}
        </div>
      )}

      {selectedCurrency && (
        <div className="mb-6">
          <DataTable<WalletMovementFull>
            title={`Opérations · Solde ${selectedCurrency}`}
            subtitle={`Tous les mouvements sur les wallets ${selectedCurrency} du marchand`}
            columns={currencyMovementsCols}
            data={currencyMovements}
            rowKey={(m) => m.id}
            loading={currencyMovementsLoading}
            toolbarActions={
              <button onClick={() => setSelectedCurrency(null)} className="btn btn-light btn-sm">
                Fermer
              </button>
            }
            emptyMessage={`Aucun mouvement sur les wallets ${selectedCurrency}`}
          />
        </div>
      )}

      <DataTable<Wallet>
        title="Soldes des wallets"
        subtitle="Cliquez sur une ligne pour voir les mouvements"
        columns={walletCols}
        data={filteredWallets}
        rowKey={(w) => w.id}
        loading={loading}
        onRowClick={(w) => setSelected(w)}
        filtersSlot={
          <>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as typeof typeFilter)}
              className="form-input py-2 px-3 text-sm w-auto"
            >
              <option value="">Tous types</option>
              <option value="mobile_money">Mobile Money</option>
              <option value="cards">Cartes</option>
            </select>
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value as typeof currencyFilter)}
              className="form-input py-2 px-3 text-sm w-auto"
            >
              <option value="">Toutes devises</option>
              <option value="CDF">CDF</option>
              <option value="USD">USD</option>
            </select>
          </>
        }
      />

      {selected && (
        <div className="mt-6">
          <DataTable<WalletMovement>
            title={`Mouvements — ${selected.business_name} · ${selected.type === "mobile_money" ? "Mobile Money" : "Cartes"} ${selected.currency}`}
            subtitle="Historique des crédits et débits (ledger append-only)"
            columns={movementCols}
            data={movements}
            rowKey={(m) => m.id}
            loading={movementsLoading}
            toolbarActions={
              <button onClick={() => setSelected(null)} className="btn btn-light btn-sm">
                Fermer
              </button>
            }
            emptyMessage="Aucun mouvement"
          />
        </div>
      )}
    </div>
  );
}

function sumByCurrency<T>(
  items: T[],
  extract: (item: T) => { currency: string; amount_minor: number },
): CurrencyVolume[] {
  const map = new Map<string, number>();
  for (const item of items) {
    const { currency, amount_minor } = extract(item);
    map.set(currency, (map.get(currency) ?? 0) + amount_minor);
  }
  return Array.from(map, ([currency, amount_minor]) => ({ currency, amount_minor }));
}

function CurrencyStatCard({
  currency,
  availableMinor,
  reservedMinor,
  selected = false,
  onClick,
}: {
  currency: string;
  availableMinor: number;
  reservedMinor: number;
  selected?: boolean;
  onClick?: () => void;
}) {
  const gradient =
    currency === "USD"
      ? "from-emerald-500 to-emerald-700"
      : currency === "CDF"
      ? "from-sky-500 to-sky-700"
      : "from-slate-500 to-slate-700";
  const ring = selected ? "ring-4 ring-white/60 ring-offset-2 ring-offset-transparent" : "";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`card overflow-hidden relative bg-gradient-to-br ${gradient} text-left transition-transform hover:-translate-y-0.5 hover:shadow-lg cursor-pointer ${ring}`}
      aria-pressed={selected}
    >
      <div className="px-6 py-5 text-white">
        <div className="flex items-center justify-between">
          <div className="text-sm opacity-90">Solde {currency}</div>
          <span className="text-[10px] font-bold tracking-wider bg-white/20 rounded px-2 py-0.5">
            {currency}
          </span>
        </div>
        <div className="text-3xl font-bold mt-2">{formatAmount(availableMinor, currency)}</div>
        <div className="text-xs opacity-80 mt-1">
          Réservé : {formatAmount(reservedMinor, currency)}
        </div>
        <div className="text-[10px] uppercase tracking-wider opacity-70 mt-2">
          {selected ? "Cliquer pour fermer" : "Cliquer pour voir les opérations"}
        </div>
      </div>
    </button>
  );
}
